import { createContext, useContext, useState, type JSX, type ReactNode } from 'react';

import type { LangCode } from './language-toggle.js';

export type { LangCode };

const LANG_STORAGE_KEY = 'access_lang';

function getInitialLang(): LangCode {
  try {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'en' || stored === 'es') return stored;
  } catch {
    // Safari private mode throws DOMException on localStorage access — fall through to detection
  }
  if (navigator.language.startsWith('es')) return 'es';
  return 'en';
}

function persistLang(lang: LangCode): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // Safari private mode — ignore storage failure, preference applies for this session only
  }
}

const LangContext = createContext<LangCode>('en');
const SetLangContext = createContext<(lang: LangCode) => void>(() => undefined);

/**
 * useLang — consume the current language from AppShell context.
 * Use in child components that need to respond to language changes.
 */
export function useLang(): LangCode {
  return useContext(LangContext);
}

/**
 * useSetLang — change the current language (persists to localStorage via AppShell).
 * Pages render their own LanguageToggle inside their chrome and wire it up with this.
 */
export function useSetLang(): (lang: LangCode) => void {
  return useContext(SetLangContext);
}

interface AppShellProps {
  children: ReactNode;
}

/**
 * AppShell — lang state owner for all routes.
 * Reads initial value from localStorage, persists changes, and provides
 * lang + setter to children via context. Renders no chrome of its own —
 * each page owns its full brand chrome (topbar, navbar, footer).
 *
 * No portal nav chrome (access-landing is not a portal app).
 * No auth provider (access-landing is fully public).
 */
export function AppShell({ children }: AppShellProps): JSX.Element {
  const [lang, setLang] = useState<LangCode>(getInitialLang);

  function handleLangChange(newLang: LangCode): void {
    persistLang(newLang);
    setLang(newLang);
  }

  return (
    <LangContext.Provider value={lang}>
      <SetLangContext.Provider value={handleLangChange}>{children}</SetLangContext.Provider>
    </LangContext.Provider>
  );
}
